import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import { ConditionName } from "interfaces";
import { checkParentTarget, conditions, videoTag } from "helpers";
import { brightnessLog, sendFluentLog, volumeLog } from 'logs';
import { createSettings } from "redux-share/actions/user.actions";
import { setActiveElements } from "redux-share/slices/controllerSlice";
import { setUserSettings } from "redux-share/slices/userSlice";
import IMAGES from "../../../../../images/importImages";
import store from "redux-share/store";
import { shallowEqual } from 'react-redux';
import dynamic from "next/dynamic";

const Mute = dynamic(() => import("../../icon-components/mute"));
const UnMute = dynamic(() => import("../../icon-components/unMute"));

const TouchVolumeBrightness = () => {

    const [type, setType] = useState<string>("");
    
    const dispatch = useAppDispatch()
    const { settings } = useAppSelector(({ user: { settings } }) => ({ settings }), shallowEqual);

    const primaryBrightnessValueRef = useRef<number>(settings.brightness);
    const primaryVolumeValueRef = useRef<number>(settings.volume);
    const typeRef = useRef<string>(type);
    
    useEffect(() => {
        typeRef.current = type;
    }, [type])


    useEffect(() => {

        let startPositionY = 0;

        const handleTouchStartScreen = (event:any) => {
            if (conditions(ConditionName.hidePopovers) && !checkParentTarget(event, "time-progress-area")) startPositionY = event.touches[0].clientY
        }

        const handleTouchMoveScreen = (event: any) => {
            if (conditions(ConditionName.hidePopovers) && !checkParentTarget(event, "time-progress-area")) {
                event.stopPropagation();
                if (event.touches[0].clientX > window.innerWidth / 2) handleTouchMove(event, "volume", startPositionY, window.innerHeight);
                else if (event.touches[0].clientX < window.innerWidth / 2) handleTouchMove(event, "brightness", startPositionY, window.innerHeight);
            }
        }

        const handleTouchEndScreen = () => {
            const { settings } = store.getState().user;
            if (typeRef.current === "brightness" && primaryBrightnessValueRef.current !== settings.brightness) {
                sendFluentLog(brightnessLog(primaryBrightnessValueRef.current, settings.brightness));
                primaryBrightnessValueRef.current = settings.brightness;
                createSettings({ ...settings, brightness: settings.brightness });
            } else if (typeRef.current === "volume" && primaryVolumeValueRef.current !== settings.volume) {
                sendFluentLog(volumeLog(primaryVolumeValueRef.current, settings.volume));
                primaryVolumeValueRef.current = settings.volume;
                createSettings({ ...settings, volume: settings.volume });
            }
            setTimeout(() => setType(""), 500);
        }
        
        document.addEventListener("touchstart", (event:any) => handleTouchStartScreen(event))
        document.addEventListener("touchmove",(event:any) => handleTouchMoveScreen(event))
        document.addEventListener("touchend", () => handleTouchEndScreen());

        return () => {
            document.removeEventListener("touchstart", (event: any) => handleTouchStartScreen(event))
            document.removeEventListener("touchmove", (event: any) => handleTouchMoveScreen(event))
            document.removeEventListener("touchend", () => handleTouchEndScreen());
        }
    },[])

    useEffect(() => {
        if(settings.volume === 0 && !type) primaryVolumeValueRef.current = 0;
    },[settings.volume])

    const handleTouchMove = (event:any, type:string, startPositionY:number, area: number) => {
        const { settings } = store.getState().user;
        try {
            dispatch(setActiveElements(false))
            event.stopPropagation();
            setType(type);
            const { height } = document.getElementsByClassName("pl-bar")[0].getBoundingClientRect();
            if (type === "volume") {
                const value = settings.volume + (((startPositionY - event.touches[0].clientY) / 40) * height) / area;
                let volumeCount:number;
                if (value > 100) volumeCount = 100;
                else if (value < 0) volumeCount = 0
                else volumeCount = Math.ceil(Math.abs(value));
                dispatch(setUserSettings({...settings, volume: volumeCount}))
                videoTag().volume = volumeCount / 100;
            } else if (type === "brightness") {
                const value = settings.brightness + (((startPositionY - event.touches[0].clientY) / 40) * height) / area;
                if (value > 100) dispatch(setUserSettings({...settings, brightness: 100}));
                else if (value < 0) dispatch(setUserSettings({ ...settings, brightness: 0 }))
                else dispatch(setUserSettings({ ...settings, brightness: Math.ceil(Math.abs(value)) }));
            }
        } catch (error) {
            return false
        }
    }

    return (
        <div id="volume-brightness">
            <div className={`pl-fixed pl-w-screen pl-h-screen pl-top-0 pl-right-0 pl-bg-black`} style={{ opacity: settings.brightness < 10 ? 0.9 : 1 - settings.brightness / 100 }}></div>
            <div className="pl-absolute pl-top-0 pl-right-0 pl-w-full pl-h-full pl-flex pl-justify-between pl-items-center">
                <div className="pl-flex pl-items-center pl-flex-1 pl-h-full pl-pr-6">
                    <div id={"volume-progressbar"} className={`pl-bar pl-relative pl-w-6 pl-h-[200px] ${type === "volume" && "pl-bg-gray-300"} pl-rounded-[50px] pl-overflow-hidden pl-max-h-[80vh] pl-flex pl-items-end pl-justify-end`}>
                        {type === "volume" && (
                            <>
                                <div className='pl-absolute pl-w-full pl-bg-secondary-600 pl-rounded-b-[50px]' style={{ height: `${settings.volume}%` }}/>
                                <div className="pl-mb-[7px] pl-ml-0.5 pl-flex pl-gap-1 pl-items-center">
                                    <div className="pl-relative pl-flex pl-items-center">
                                        {settings.volume > 0 && <div className='pl-absolute pl-right-[2px] pl-w-2 pl-h-2 pl-rounded-full pl-flex pl-items-center pl-justify-center pl-border-2 pl-border-transparent pl-border-r-2 pl-border-r-z-yellow-light'/>}
                                        {settings.volume > 33 && <div className='pl-absolute pl-right-[-1px] pl-w-3 pl-h-3 pl-rounded-full pl-flex pl-items-center pl-justify-center pl-border-2 pl-border-transparent pl-border-r-2 pl-border-r-z-yellow-light'/>}
                                        {settings.volume > 66 && <div className='pl-absolute pl-right-[-4px] pl-w-4 pl-h-4 pl-rounded-full pl-flex pl-items-center pl-justify-center pl-border-2 pl-border-transparent pl-border-r-2 pl-border-r-z-yellow-light'/>}
                                    </div>
                                    {settings.volume === 0 ? <Mute className="pl-z-[1] pl-w-[13px] pl-h-[13px] pl-fill-dark-7" /> : <UnMute className="pl-z-[1] pl-w-[13px] pl-h-[13px] pl-fill-dark-7" />}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {type && (
                    <div className="pl-bg-secondary-700/[0.8] pl-rounded-full pl-w-[54px] pl-h-[54px] pl-flex pl-items-center pl-justify-center pl-text-[32px] pl-font-normal pl-text-white">
                        {type === "volume" ? settings.volume : settings.brightness}
                    </div>
                )}
                <div className="pl-flex pl-items-center pl-justify-end pl-flex-1 pl-h-full pl-pl-6">
                    <div id={"brightness-progressbar"} className={`pl-bar pl-relative pl-w-6 pl-h-[200px] ${type === "brightness" && "pl-bg-gray-300"} pl-rounded-[50px] pl-overflow-hidden pl-max-h-[80vh] pl-flex pl-items-end pl-justify-end`}>
                        {type === "brightness" && (
                            <>
                                <div className='pl-absolute pl-w-full pl-bg-primary-600 pl-rounded-b-[50px]' style={{ height: `${settings.brightness}%` }}/>
                                <div className="pl-mb-[7px] pl-ml-0.5 pl-flex pl-gap-1 pl-items-center" style={{ opacity: settings.brightness / 100 }}>
                                    <Image src={IMAGES.sun} className="pl-flex-1" width={20} height={20} />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TouchVolumeBrightness;